import { motion } from 'motion/react';
import { memo, useEffect, useState } from 'react';
import { Carousel, CarouselContent, CarouselItem } from '@music-vine/cadence/ui';
import { FadeAway } from '@music-vine/cadence/ui';
import { SearchButton } from '@/features/GlobalSearch/components/search/SearchButton';
import { getTrendingResultUrl } from '@/features/GlobalSearch/utils/getTrendingSearches';
import useTWScreens from '@/hooks/ui/useTWScreens';
import { cn } from '@/lib/utils';
import type { CarouselApi } from '@music-vine/cadence/ui';
import type { AssetType } from '@/features/GlobalSearch/types/index';
import type { GenericSearchResult } from '@/features/SearchPage/types';
import type { SearchResponseHit } from '@music-vine/setlist';
interface PopularSearchCarouselProps {
  assetType: AssetType;
  closePopup: () => void;
  getHref?: (slug: string) => string;
  hits?: SearchResponseHit<GenericSearchResult>[];
  onNameClick: (content: string | undefined, endSlug: string | undefined, assetType: AssetType | 'general') => void;
  testId?: string;
}
export const PopularSearchCarousel = memo(({
  hits,
  assetType,
  closePopup,
  onNameClick,
  testId,
  getHref
}: PopularSearchCarouselProps) => {
  const {
    isMd
  } = useTWScreens();

  // Add state for carousel scroll position
  const [scrollState, setScrollState] = useState<{
    canScrollNext: boolean;
    canScrollPrev: boolean;
  }>({
    canScrollNext: false,
    canScrollPrev: false
  });

  // Add API ref for carousel
  const [api, setApi] = useState<CarouselApi>();

  // Use motion.div only on desktop, regular div on mobile to avoid iOS Safari touch issues
  const AnimatedItem = isMd ? motion.div : 'div';
  useEffect(() => {
    if (!api) {
      return;
    }
    const onSelect = () => {
      requestAnimationFrame(() => {
        setScrollState({
          canScrollNext: api.canScrollNext(),
          canScrollPrev: api.canScrollPrev()
        });
      });
    };

    // Check initial scroll state
    onSelect();

    // Listen for changes
    api.on('select', onSelect);
    api.on('reInit', onSelect);
    api.on('resize', onSelect);
    return () => {
      api.off('select', onSelect);
      api.off('reInit', onSelect);
      api.off('resize', onSelect);
    };
  }, [api]);
  if (!hits?.length) {
    return null;
  }
  return <Carousel className={cn('w-full overflow-hidden',
  // Disable all animations on mobile to fix iOS Safari touch issues
  'max-md:[&_*]:animation-none! max-md:[&_*]:transition-none!')} opts={{
    align: 'start',
    dragFree: true
  }} setApi={setApi}>
        <CarouselContent className="mx-4">
          {hits.map(({
        document
      }, idx) => <CarouselItem className="basis-auto pr-3 pl-0" key={document.id}>
              <AnimatedItem animate={isMd ? {
          opacity: 1
        } : undefined} initial={isMd ? {
          opacity: 0
        } : undefined} transition={isMd ? {
          duration: 0.15,
          delay: idx * 0.05
        } : undefined}>
                <SearchButton assetType={assetType} closePopup={closePopup} href={getHref ? getHref(document.slug ?? '') : getTrendingResultUrl({
            name: document.name ?? '',
            type: assetType
          })} id={document.id} name={document.name ?? ''} onNameClick={onNameClick} testId={testId} />
              </AnimatedItem>
            </CarouselItem>)}
        </CarouselContent>
        <FadeAway className="z-0 w-6 via-white dark:via-gray-950" direction="right" show={scrollState.canScrollNext} />
        <FadeAway className="z-0 w-6 via-white dark:via-gray-950" direction="left" show={scrollState.canScrollPrev} />
      </Carousel>;
});
PopularSearchCarousel.displayName = 'PopularSearchCarousel';